import { DataGroupInfoHeader } from '@base/classes/BaseClasses';
import { formatDate } from "devextreme/localization";
import { FrameworkService } from "../framework/framework.service";
import { RC } from "../framework/resources.service";
import { EnumLanguages } from '../framework/sysenums.enum';
import { SysGlobals } from "../framework/sysglobals";
import { AppInjector } from "../injector/injector.module";
import "./DateExtensions";

export class DateHelpers {


    //Return only date part of the value
    public static ToDateOnly(value: Date): Date {
        var date = new Date(value);
        date.setHours(0, 0, 0, 0);
        return date;
    }

    //Return only time part of the value
    public static ToTimeOnly(value: Date): Date {
        return new Date(1900, 0, 1, value.getHours(), value.getMinutes(), value.getSeconds());
    }

    public static DiffInSeconds(d1: Date, d2: Date): number {
        return (d2.valueOf() - d1.valueOf()) / (1000 * 1);
    }

    public static DiffInMinutes(d1: Date, d2: Date): number {
        return (d2.valueOf() - d1.valueOf()) / (1000 * 60);
    }

    public static DiffInHours(d1: Date, d2: Date): number {
        var x1 = new Date(0, 0, 0, d1.getHours(), d1.getMinutes());
        var x2 = new Date(0, 0, 0, d2.getHours(), d2.getMinutes());
        return (x2.valueOf() - x1.valueOf()) / (1000 * 3600);
    }

    public static DiffInDays(d1: Date, d2: Date): number {
        return (d2.ToDate().valueOf() - d1.ToDate().valueOf()) / (1000 * 3600 * 24);
    }

    public static DiffInWeeks(d1: Date, d2: Date): number {
        return (d2.ToDate().valueOf() - d1.ToDate().valueOf()) / 604800000;
    }

    public static DiffInMonthes(d1: Date, d2: Date): number {
        let months = 0;
        months = (d2.getFullYear() - d1.getFullYear()) * 12;
        months -= d1.getMonth();
        months += d2.getMonth();
        return months <= 0 ? 0 : months;
    }

    public static IsSameDate(d1: Date, d2: Date) {
        return (d1.getFullYear() == d2.getFullYear() && d1.getMonth() == d2.getMonth() && d1.getDate() == d2.getDate());
    }

    public static BOM(d1: Date) {
        return new Date(d1.getFullYear(), d1.getMonth(), 1);
    }

    public static BOY(d1: Date) {
        return new Date(d1.getFullYear(), 0, 1);
    }

    public static EOY(d1: Date) {
        return new Date(d1.getFullYear(), 11, 1);
    }

    public static EOM(d1: Date) {
        return new Date(d1.getFullYear(), d1.getMonth() + 1, 0);
    }

    public static GetDayName(iDay: number): string {
        switch (iDay) {
            case 1: return RC("lblDay1");
            case 2: return RC("lblDay2");
            case 3: return RC("lblDay3");
            case 4: return RC("lblDay4");
            case 5: return RC("lblDay5");
            case 6: return RC("lblDay6");
            case 7: return RC("lblDay7");
        }
    }

    public static GetDayNameShort(iDay: number): string {
        switch (iDay) {
            case 1: return RC("lblDay1s");
            case 2: return RC("lblDay2s");
            case 3: return RC("lblDay3s");
            case 4: return RC("lblDay4s");
            case 5: return RC("lblDay5s");
            case 6: return RC("lblDay6s");
            case 7: return RC("lblDay7s");
        }
    }

    public static GetDateApproximate(date: Date, bShowDate: boolean = true): string {
        var framework = AppInjector.get(FrameworkService);
        if (date == null) return RC("lblDateIsNull");
        var sApproximate: string = this.GetDateApproximateInternal(date);
        var sFormattedDate: string = this.IsSameDate(date, new Date()) && this.IsHasTime(date)
            ? formatDate(date, framework.CompanyInfo.TimeFormatShort)
            : formatDate(date, framework.CompanyInfo.DateFormatShort);
        var sFinalDate: string = bShowDate ? `(${sFormattedDate})` : '';
        return RC("lblDateApproximate", sApproximate, RC('lblAgo'), sFinalDate)
        //if (SysGlobals.Language == EnumLanguages.English) return `${sApproximate} ${RC('lblAgo')} ${sFinalDate}`;
        //if (SysGlobals.Language == EnumLanguages.Spanish) return `${sApproximate} ${RC('lblAgo')} ${sFinalDate}`;
        //if (SysGlobals.Language == EnumLanguages.Hebrew) return `${RC('lblAgo')} ${sApproximate} ${sFinalDate}`;
    }


    public static IsHasTime(date: Date) {
        return date.getHours() != 0 || date.getMinutes() != 0 || date.getSeconds() != 0;
    }

    private static GetDateApproximateInternal(date: Date): string {
        const now = new Date();
        const diff = now.getTime() - date.getTime();
        const nMinutes = diff / 1000 / 60;
        const nHours = nMinutes / 60;
        const nDays = nHours / 24;
        const nWeeks = nDays / 7;
        const nMonths = nDays / 30;
        const nYears = nMonths / 12;


        // Less then 1 day
        if (this.IsHasTime(date)) {
            if (nMinutes <= 1) return RC("f702");
            if (nMinutes <= 5) return RC("f703");
            if (nMinutes <= 10) return RC("f704");
            if (nMinutes <= 15) return RC("f705");
            if (nMinutes <= 30) return RC("f706");
            if (nMinutes <= 60) return RC("f707");
            if (nMinutes <= 90) return RC("f709");
            if (nMinutes <= 135) return RC("f710");
            if (nDays <= 1) return RC("f711", Math.floor(nHours));
        }
        else {
            if (nDays <= 1) return RC("f725");
        }

        // Less then 1 week
        if (nDays <= 2) return RC("f712");
        if (nDays <= 3) return RC("f713");
        if (nWeeks <= 1) return RC("f714", Math.floor(nDays));

        // Less then 1 month
        if (nWeeks <= 2) return RC("f715")
        if (nWeeks <= 3) return RC("f716")
        if (nMonths <= 1) return RC("f717", Math.floor(nWeeks))

        // Less then 1 year
        if (nMonths <= 2) return RC("f718")
        if (nMonths <= 3) return RC("f720")
        if (nYears <= 1) return RC("f721", Math.floor(nMonths))

        // Years
        if (nYears <= 2) return RC("f722")
        if (nYears <= 3) return RC("f723")

        return RC("f724", Math.floor(nYears));
    }

    public static GetGroupsByDate(): DataGroupInfoHeader[] {
        let groups =
            [
                new DataGroupInfoHeader(RC('lblToday'), '-1'),
                new DataGroupInfoHeader(RC('lblYesterday'), '-2'),
                new DataGroupInfoHeader(RC('lblLastWeek'), '-7'),
                new DataGroupInfoHeader(RC('lblLastMonth'), '-30'),
                new DataGroupInfoHeader(RC('lblBefore'), '-999')
            ]

        return groups;
    }


    public static AddDay(date: Date, count: number) {
        const newDate = new Date(date.valueOf());
        newDate.setDate(date.getDate() + count);
        return newDate;
    }

    public static AddWeek(date: Date, count: number) {
        const newDate = new Date(date.valueOf());
        newDate.setDate(date.getDate() + count * 7);
        return newDate;
    }

    public static AddMonth(date: Date, count: number) {
        const newDate = new Date(date.valueOf());
        newDate.setMonth(date.getMonth() + count);
        return newDate;
    }
}